import { Card, CardContent } from '@mui/material';
import { Loading, SimpleForm, TextInput, Title, useGetOne, useNotify, useRefresh } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { jsonFormat, optionalJsonParse, validateOptionalJsonObject } from '../../lib/jsonInput';
import { changedConfigFields, errorMessage, PartnerRecord, partnerConfigBody, savePartnerConfig } from '../../lib/partners';

export interface PartnerConfigEditProps {
  mode: 'create' | 'edit';
}

const PartnerConfigEdit = ({ mode }: PartnerConfigEditProps) => {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const notify = useNotify();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: partner, isLoading } = useGetOne<PartnerRecord>('partners', { id: uuid });

  if (isLoading) return <Loading />;

  const showConfigTab = `/partners/${encodeURIComponent(uuid)}/show/config`;

  const handleSubmit = async (values: FieldValues) => {
    try {
      const body = partnerConfigBody(values);
      await savePartnerConfig(uuid, mode === 'edit' && partner?.config ? changedConfigFields(body, partner.config) : body, mode);
      notify(mode === 'create' ? 'Config created' : 'Config saved', { type: 'success' });
      refresh();
      navigate(showConfigTab);
    } catch (error) {
      notify(errorMessage(error), { type: 'error' });
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <Title title={`${mode === 'create' ? 'Create' : 'Edit'} config — ${partner?.name ?? uuid}`} />
      <CardContent sx={{ p: 0 }}>
        <SimpleForm record={mode === 'edit' && partner?.config ? partner.config : {}} onSubmit={handleSubmit}>
          <TextInput source="domain" label="Domain" fullWidth helperText="Optional" />
          <TextInput source="cssUrl" label="CSS URL" fullWidth helperText="Optional" />
          <TextInput source="payrollUrl" label="Payroll URL" fullWidth helperText="Optional" />
          <TextInput source="payrollUrlAuthStrategy" label="Payroll URL auth strategy" helperText="Optional" />
          <TextInput source="extraOptions" label="Extra options (JSON)" multiline fullWidth minRows={8} format={jsonFormat} parse={optionalJsonParse} validate={validateOptionalJsonObject} helperText="Optional — valid JSON object (e.g. {})" />
        </SimpleForm>
      </CardContent>
    </Card>
  );
};

export default PartnerConfigEdit;
